"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { Button, ButtonDock, Header, InputField, MessageCard } from "@dalmatech/ui"
import useForm from "@/shared/hooks/use-form"
import { useMediaQuery } from "@/shared/hooks/use-media-query"
import { ROUTES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { useRouter } from "next/navigation"
import React from "react"
import { type SubmitHandler } from "react-hook-form"

import { getPetById, getPetIndex, getPetOrder } from "../../helpers/helpers"
import { PetNameSchema, petNameSchema } from "../../schemas/pet-name.schema"
import * as styles from "./pet-name.styled"

export const PetNamePage = () => {
  const router = useRouter()
  const [state, send] = useActor(newSubscriptionFlowService)
  const [isLoading, setIsLoading] = React.useState(false)
  const isViewportBiggerThanMobile = useMediaQuery("(min-width: 1024px)")
  const { t } = useClientTranslation("pet-name-page")
  const currentPet = getPetById(state.context.quote?.pets || [], state.context.currentPetId)
  const currentPetIdx = getPetIndex(state.context.quote?.pets || [], currentPet?.id)
  const safePetIndex = currentPetIdx >= 0 ? currentPetIdx : 0
  const currentPetName = currentPetIdx >= 0 ? state.context.quote?.pets?.[currentPetIdx]?.name : ""
  const { register, handleSubmit, formState } = useForm(petNameSchema, {
    defaultValues: {
      name: currentPetName || "",
    },
  })

  const onSubmit: SubmitHandler<PetNameSchema> = (values) => {
    setIsLoading(true)
    send({
      type: "ADD_PET_NAME",
      petName: values.name,
      petId: currentPet?.id || null,
    })
    router.push(ROUTES.petInfo.path, { scroll: true })
  }

  return (
    <div className={styles.root}>
      <Header>
        {safePetIndex > 0
          ? t("title.multiple-pets", { petOrder: getPetOrder(safePetIndex) })
          : isViewportBiggerThanMobile
          ? t("title.single-pet")
          : t("title.single-pet-mobile")}
      </Header>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.form}>
        <InputField
          type="text"
          placeholder={t("pet-name-placeholder")}
          error={formState.errors.name?.message}
          {...register("name")}
        />

        <MessageCard animated={false}
          title={t("info-card.title")}
          description={t("info-card.description")}
        />
        <ButtonDock isSingleButton isFixedOnMobile>
          <Button
            type="submit"
            disabled={!formState.isValid}
            className="w-[36.750rem]"
            size="lg"
            isLoading={isLoading}
          >
            {state.context.quote?.pets?.length >= 1 ? t("cta.multiple-pets") : t("cta.single-pet")}
          </Button>
        </ButtonDock>
      </form>
    </div>
  )
}
